/**
 * @engine/vfx — shared pieces of the generated stage main() functions.
 */

import type { ParticleAttributes } from '../../types';
import { computeModuleHeader } from '../codegen/slangTypes';
import type { CallEmissionResult } from '../codegen/CallEmitter';
import type { BakedSampler } from '../codegen/Samplers';

/** Env builtins the init stage main() can provide. */
export const INIT_STAGE_BUILTINS: ReadonlySet<string> = new Set([
  'particle', 'idx', 'localIdx', 'spawnCount', 'sourceEvent',
  'deltaTime', 'simulationTime', 'emitterVelocity',
]);

/** Env builtins the update stage main() can provide. */
export const UPDATE_STAGE_BUILTINS: ReadonlySet<string> = new Set([
  'particle', 'idx', 'deltaTime', 'simulationTime', 'emitterVelocity',
]);

/**
 * Emit `let` bindings for optional builtins a stage provides on demand
 * (always-present ones like `particle`/`idx` are part of the stage skeleton).
 */
export function optionalBuiltinPrologue(
  used: ReadonlySet<string>,
  opts: { deltaTime?: boolean; simulationTime?: boolean } = {}
): string[] {
  const lines: string[] = [];
  // deltaTime/simulationTime are unconditional in the update stage (its own
  // epilogue needs them); init emits them only when nodes ask.
  if (opts.deltaTime !== false && used.has('deltaTime')) {
    lines.push('let deltaTime = uniforms.deltaTime;');
  }
  if (opts.simulationTime !== false && used.has('simulationTime')) {
    lines.push('let simulationTime = uniforms.time;');
  }
  if (used.has('emitterVelocity')) {
    lines.push('let emitterVelocity = float3(uniforms.emitterVelX, uniforms.emitterVelY, 0.0);');
  }
  return lines;
}

/**
 * GPU-event emission block (shared by triggers and death events).
 * Semantics identical to v1: reserve a slot with an atomic add, drop if full.
 */
export function eventEmitLines(eventId: number, count: number): string[] {
  return [
    '{',
    '    let eventIdx = eventCount[0].count.add(1u);',
    '    if (eventIdx < MAX_EVENTS)',
    '    {',
    '        events[eventIdx].position = particle.position;',
    '        events[eventIdx].velocity = particle.velocity;',
    '        events[eventIdx].color = particle.color;',
    `        events[eventIdx].spawnCount = ${count}u;`,
    `        events[eventIdx].eventId = ${eventId}u;`,
    '    }',
    '}',
  ];
}

/**
 * Source map of a generated stage module: 1-based line ranges → the node
 * instances whose code lives there. Used to attribute Slang diagnostics back
 * to graph nodes (editor error highlighting).
 */
export interface VFXStageSourceMap {
  readonly regions: ReadonlyArray<{
    readonly startLine: number;
    readonly endLine: number;
    /** Node instance ids owning this region. */
    readonly nodeIds: readonly string[];
    /** Node type id (for messages). */
    readonly nodeType?: string;
  }>;
}

/** Resolve a diagnostic line to the owning node instance ids (empty if structural). */
export function nodesAtLine(map: VFXStageSourceMap, line: number): readonly string[] {
  for (const region of map.regions) {
    if (line >= region.startLine && line <= region.endLine) return region.nodeIds;
  }
  return [];
}

/**
 * Assemble a full stage module: header (structs/bindings/imports), baked
 * samplers, node functions, and the stage's main(). Also builds the source
 * map (node functions + per-node call lines).
 */
export function assembleStageModule(opts: {
  attributes?: ParticleAttributes;
  emission: CallEmissionResult;
  mainLines: string[];
  workgroupSize: number;
  /** Index in mainLines where emission.lines were inserted (-1 if none). */
  emissionStartIndex?: number;
  /** Editor tweak mode: fixed tweak-array capacity (see codegen/Tweaks.ts). */
  tweakCapacity?: number;
}): { source: string; sourceMap: VFXStageSourceMap } {
  const lines: string[] = [];
  const regions: Array<{ startLine: number; endLine: number; nodeIds: string[]; nodeType?: string }> = [];
  const push = (text: string) => {
    for (const l of text.split('\n')) lines.push(l);
  };

  push(computeModuleHeader({
    attributes: opts.attributes,
    textureSlots: [...opts.emission.textureSlots],
    tweakCapacity: opts.tweakCapacity,
  }));

  // Baked gradient/curve samplers (dedupe by name across functions; attribute
  // each to the nodes whose specialization baked it)
  const samplers = new Map<string, { sampler: BakedSampler; users: Set<string> }>();
  for (const fn of opts.emission.functions) {
    for (const s of fn.samplers) {
      const entry = samplers.get(s.name) ?? { sampler: s, users: new Set<string>() };
      for (const u of fn.users) entry.users.add(u);
      samplers.set(s.name, entry);
    }
  }
  if (samplers.size > 0) {
    push('');
    push('// Baked gradient/curve samplers');
    for (const { sampler, users } of samplers.values()) {
      const startLine = lines.length + 1;
      push(sampler.code);
      regions.push({ startLine, endLine: lines.length, nodeIds: [...users] });
    }
  }

  if (opts.emission.functions.length > 0) {
    push('');
    push('// Node functions');
    for (const fn of opts.emission.functions) {
      const startLine = lines.length + 1;
      push(fn.code);
      regions.push({ startLine, endLine: lines.length, nodeIds: [...fn.users], nodeType: fn.nodeType });
    }
  }

  push('');
  push('[shader("compute")]');
  push(`[numthreads(${opts.workgroupSize}, 1, 1)]`);
  push('void main(uint3 id : SV_DispatchThreadID)');
  push('{');
  const mainBodyFirstLine = lines.length + 1; // 1-based line of mainLines[0]
  for (const line of opts.mainLines) {
    lines.push(line === '' ? '' : `    ${line}`);
  }
  push('}');
  push('');

  // Per-node call lines inside main()
  const emissionStart = opts.emissionStartIndex ?? -1;
  if (emissionStart >= 0) {
    const owners = opts.emission.lineOwners;
    for (let i = 0; i < owners.length; i++) {
      const owner = owners[i];
      if (!owner) continue;
      const line = mainBodyFirstLine + emissionStart + i;
      regions.push({ startLine: line, endLine: line, nodeIds: [owner] });
    }
  }

  return { source: lines.join('\n'), sourceMap: { regions } };
}

/** Indent helper for nested blocks inside mainLines. */
export function block(lines: string[], indentLevel = 1): string[] {
  const pad = '    '.repeat(indentLevel);
  return lines.map(l => (l === '' ? '' : pad + l));
}
